import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, normalizeClass as _normalizeClass, openBlock as _openBlock, createBlock as _createBlock, createVNode as _createVNode, createElementVNode as _createElementVNode, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "employee-select" }
const _hoisted_2 = { class: "employee-select__duration-container" }
const _hoisted_3 = ["disabled"]

import { computed, toRefs, watch, nextTick, ref, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { helpers, required } from '@vuelidate/validators';
import type { EmployeeData, CustomFilterFunction } from './types';

interface Props {
  modelValue: EmployeeData;
  customFilter?: CustomFilterFunction;
  required?: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'a-employee-select',
  props: {
    modelValue: {},
    customFilter: { type: Function, default: undefined },
    required: { type: Boolean }
  },
  emits: ["update:modelValue", "clearEmployee"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props;

const emit = __emit;

const store = useStore();

const employeeSelectRef = ref<any>(null);

// Force re-render key
const componentKey = ref(0);

// Ensure all employees have a displayable name
const enrichedEmployees = computed(() => {
  const employees = store.getters['tm/enrichedEmployees'] || [];
  return employees.map((emp: any) => ({
    ...emp,
    _fullName:
      emp._fullName ||
      `${emp.name || ''} ${emp.surname || ''}`.trim() ||
      `Employee ${emp.id}`,
  }));
});

// Function to get item title - handles both objects and IDs
const getItemTitle = (item: any): string => {
  // If item is an object with _fullName, return it
  if (item && typeof item === 'object' && item._fullName) {
    return item._fullName;
  }

  // If item is just an ID (string or number), find the employee
  if (item && (typeof item === 'string' || typeof item === 'number')) {
    const employee = enrichedEmployees.value.find((emp) => emp.id == item);
    return employee?._fullName || `Employee ${item}`;
  }

  return '';
};

// Watch for when employees list changes to force re-render
watch(
  () => enrichedEmployees.value.length,
  (newLength, oldLength) => {
    // Force component re-render when employees are loaded
    if (newLength > 0 && newLength !== oldLength) {
      nextTick(() => {
        componentKey.value++;
      });
    }
  },
  { immediate: true },
);

// Watch for when the selected employee ID changes to ensure display updates
watch(
  () => props.modelValue.id,
  (newId) => {
    // When ID is set and we have employees, force re-render to ensure proper display
    if (newId && enrichedEmployees.value.length > 0) {
      nextTick(() => {
        componentKey.value++;
      });
    }
  },
);

// Make modelValue reactive for Vuelidate
const { modelValue } = toRefs(props);

// Custom validator: if either field is filled, both are required
const requiredWhenPaired = (fieldName: 'id' | 'duration') => {
  return (_value: any) => {
    const { id, duration } = modelValue.value;
    const hasAnyData = !!(id || duration);

    // If no data at all, this is valid (both fields optional)
    if (!hasAnyData) {
      return true;
    }

    // If there's any data, this field must be filled
    if (fieldName === 'id') {
      return !!id;
    } else {
      return duration !== null && duration !== undefined;
    }
  };
};

// Vuelidate rules - only validate if employee has partial data
const rules = computed(() => {
  return props.required
    ? {
        id: { required },
        duration: { required },
      }
    : {
        id: {
          requiredWhenPaired: helpers.withMessage(
            'Vyberte zamestnanca',
            requiredWhenPaired('id'),
          ),
        },
        duration: {
          requiredWhenPaired: helpers.withMessage(
            'Zadajte časový odhad',
            requiredWhenPaired('duration'),
          ),
        },
      };
});

const v$ = useVuelidate(rules.value, modelValue, { $autoDirty: false });

const handleUpdateEmployee = (value: number) => {
  emit('update:modelValue', {
    ...props.modelValue,
    id: value,
  });
};

const handleUpdateDuration = (value: string) => {
  const parsedValue = parseFloat(value);
  emit('update:modelValue', {
    ...props.modelValue,
    duration: isNaN(parsedValue) ? null : parsedValue,
  });
};

const handleClearEmployee = () => {
  emit('clearEmployee');
};

// Expose validate method
const validate = async (required = false) => {
  // If required, check that both fields are set
  if (required) {
    const { id, duration } = modelValue.value;
    if (!id || duration === null || duration === undefined) {
      // Explicitly mark fields as touched to show validation errors
      v$.value.$touch();
      return false;
    }
  }

  // Explicitly mark fields as touched to show validation errors
  v$.value.$touch();
  const isValid = await v$.value.$validate();
  return isValid;
};

onMounted(() => {
  setTimeout(() => {
    console.log('Employee select ref:', employeeSelectRef.value);
  }, 1000);
});

__expose({
  validate,
});

return (_ctx: any,_cache: any) => {
  const _component_v_autocomplete = _resolveComponent("v-autocomplete")!
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_icon = _resolveComponent("v-icon")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (_openBlock(), _createBlock(_component_v_autocomplete, {
      key: `employee-select-${componentKey.value}`,
      ref_key: "employeeSelectRef",
      ref: employeeSelectRef,
      "model-value": _unref(modelValue).id,
      "custom-filter": __props.customFilter,
      "match-any": true,
      label: "Priradený",
      items: enrichedEmployees.value,
      "item-title": getItemTitle,
      "item-value": "id",
      variant: "solo",
      class: _normalizeClass(["employee-select__select-field", { error: _unref(v$).id.$error }]),
      "error-messages": _unref(v$).id.$errors.map((e) => String(e.$message)),
      "onUpdate:modelValue": handleUpdateEmployee
    }, null, 8, ["model-value", "custom-filter", "items", "class", "error-messages"])),
    _createElementVNode("div", _hoisted_2, [
      _createVNode(_component_v_text_field, {
        "model-value": _unref(modelValue).duration,
        class: _normalizeClass(["employee-select__duration-field", { error: _unref(v$).duration.$error }]),
        variant: "solo",
        type: "number",
        label: "Časový odhad",
        "error-messages": _unref(v$).duration.$errors.map((e) => String(e.$message)),
        "append-inner-icon": "mdi-clock-outline",
        "onUpdate:modelValue": handleUpdateDuration
      }, null, 8, ["model-value", "class", "error-messages"]),
      _createElementVNode("button", {
        class: "employee-select__delete-button",
        disabled: !_unref(modelValue).id,
        onClick: handleClearEmployee
      }, [
        _createVNode(_component_v_icon, { icon: "mdi-close" })
      ], 8, _hoisted_3)
    ])
  ]))
}
}

})